import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Paper, TextField, Grid, IconButton, 
    CircularProgress, Alert, Breadcrumbs, Link, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createServiceConfig, updateServiceConfig, getAllServiceConfigs } from '../../services/Api';

const CreateAndModifyServiceConfigs = () => {
    const { id } = useParams();
    const location = useLocation();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        serviceCharge: location.state?.config?.serviceCharge || 0,
        gstPercentage: location.state?.config?.gstPercentage || 0,
        type: location.state?.config?.type ? String(location.state?.config?.type).toLowerCase() : '',
        isActive: location.state?.config?.isActive !== undefined ? location.state?.config?.isActive : true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (isEdit && !location.state?.config) {
            setError('Please navigate from the service configurations list to edit.');
            // Fallback: You could fetch by ID here if needed, but per request we rely on state.
        }
    }, [isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for the field being edited
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSwitchChange = (e) => {
        setFormData(prev => ({ ...prev, isActive: e.target.checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        const errors = {};
        if (!formData.type) errors.type = 'Config Type is required';
        if (formData.serviceCharge === '' || formData.serviceCharge === null || formData.serviceCharge === undefined) errors.serviceCharge = 'Service Charge is required';
        if (formData.gstPercentage === '' || formData.gstPercentage === null || formData.gstPercentage === undefined) errors.gstPercentage = 'GST Percentage is required';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (!isEdit && !formData.isActive) {
            setError('Status should be active');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setFormErrors({});
        setLoading(true);
        setError(null);

        // API expects numbers for these fields
        const payload = {
            ...formData,
            serviceCharge: Number(formData.serviceCharge),
            gstPercentage: Number(formData.gstPercentage)
        };

        try {
            // Check for duplicate type
            const configsRes = await getAllServiceConfigs();
            const configsSrc = configsRes.data || configsRes;
            const configsList = Array.isArray(configsSrc) ? configsSrc : (configsSrc.docs || configsSrc.serviceConfigs || configsSrc.data || []);
            
            const selectedType = formData.type ? String(formData.type).trim().toLowerCase() : '';
            const isDuplicate = configsList.some(config => {
                const configId = config.id || config._id;
                if (isEdit && String(configId) === String(id)) {
                    return false;
                }
                const configType = config.type ? String(config.type).trim().toLowerCase() : '';
                return configType === selectedType;
            });

            if (isDuplicate) {
                setError(`A service configuration for the type "${formData.type}" already exists.`);
                setLoading(false);
                return;
            }

            let response;
            if (isEdit) {
                response = await updateServiceConfig(id, payload);
            } else {
                response = await createServiceConfig(payload);
            }

            if (response.success || response.data || response.id) {
                navigate('/service-configs');
            } else {
                setError(response.message || 'Action failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the configuration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/service-configs'); }}>
                        Service Configuration
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Modify' : 'Create'}</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/service-configs')} sx={{ border: '1px solid', borderColor: 'text.border' }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" sx={{ fontWeight: 300 }}>
                        {isEdit ? 'Modify Configuration' : 'Create New Configuration'}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: 4, borderRadius: '15px', border: '1px solid', borderColor: 'text.border' }}>
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                required
                                fullWidth
                                size='small'
                                label="Config Type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                select
                                error={!!formErrors.type}
                                helperText={formErrors.type || "Select the service type this configuration applies to"}
                            >
                                {['food', 'grocery', 'services'].map((t) => (
                                    <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
                                        {t}
                                    </MenuItem>
                                ))}
                                {/* Dynamic fallback for existing values not in the standard list */}
                                {formData.type && !['food', 'grocery', 'services'].includes(formData.type) && (
                                    <MenuItem value={formData.type} sx={{ textTransform: 'capitalize' }}>
                                        {formData.type}
                                    </MenuItem>
                                )}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleSwitchChange}
                                        color="primary"
                                    />
                                }
                                label={formData.isActive ? "Active Status" : "Inactive Status"}
                                sx={{ mt: 1, ml: 1 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label="Service Charge (₹)"
                                name="serviceCharge"
                                type="number"
                                value={formData.serviceCharge}
                                onChange={handleChange}
                                InputProps={{ inputProps: { min: 0 } }}
                                error={!!formErrors.serviceCharge}
                                helperText={formErrors.serviceCharge}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label="GST Percentage (%)"
                                name="gstPercentage"
                                type="number"
                                value={formData.gstPercentage}
                                onChange={handleChange}
                                InputProps={{ inputProps: { min: 0, max: 100 } }}
                                error={!!formErrors.gstPercentage}
                                helperText={formErrors.gstPercentage}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button 
                                variant="outlined" 
                                sx={{ borderRadius: '8px', px: 4 }}
                                onClick={() => navigate('/service-configs')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                sx={{ borderRadius: '8px', px: 4, backgroundColor: 'primary.main' }}
                                disabled={loading}
                            >
                                {isEdit ? 'Update Configuration' : 'Save Configuration'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default CreateAndModifyServiceConfigs;
